/**
 * Razorpay 1-Click Quick Address & Checkout Modal.
 * Matches the luxury 1-click checkout design (Mason Home style).
 * Collects Delivery Address & Contact, then opens Razorpay payment options.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Lock, CheckCircle2, ArrowRight, ShieldCheck, ShieldAlert } from "lucide-react";
import { formatPrice } from "@/components/forms/price-display";
import { openRazorpayMagicCheckout } from "@/lib/razorpay";
import {
  saveUserOrder,
  saveAddress,
  getStoredAddress,
  type UserOrder,
} from "@/features/orders/store/orders-store";
import { type AdminOrderRecord } from "@/features/admin/orders/store/admin-orders-store";
import { useCurrentUser } from "@/hooks/use-auth";
import { useCart } from "@/features/cart/store/cart-store";
import { useNavigate } from "@tanstack/react-router";
import {
  createServerRazorpayOrder,
  verifyServerPayment,
} from "@/features/checkout/services/checkout-service";
import { calculateOrderPricing } from "@/lib/pricing-calculator";
import { products } from "@/lib/products";
import { useStoreShippingSettings, useStoreTaxSettings } from "@/lib/store-settings";
import {
  checkIsCustomerBlocked,
  isCustomerBlockedSync,
} from "@/features/admin/customers/store/admin-customers-store";
import { trackMetaPurchase } from "@/lib/meta-pixel";

type RazorpayAddressModalProps = {
  open: boolean;
  onClose: () => void;
  items: Array<{
    id: string;
    name: string;
    price: number;
    qty: number;
    image?: string;
  }>;
  totalAmount: number;
};

export function RazorpayAddressModal({
  open,
  onClose,
  items,
  totalAmount,
}: RazorpayAddressModalProps) {
  const { user } = useCurrentUser();
  const { clear } = useCart();
  const navigate = useNavigate();

  const [pincode, setPincode] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [area, setArea] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [blockedError, setBlockedError] = useState<string | null>(null);

  const shippingSettings = useStoreShippingSettings();
  const taxSettings = useStoreTaxSettings();

  // Guarantee items always reflect authoritative catalog prices even if stale props were passed
  const resolvedItems = useMemo(() => {
    return items.map((i) => {
      const p = products.find((prod) => prod.id === i.id || (prod.slug && prod.slug === i.id));
      return {
        ...i,
        price: p ? p.price : i.price,
        name: p ? p.name : i.name,
        image: p?.image || i.image,
      };
    });
  }, [items]);

  const pricing = calculateOrderPricing(
    resolvedItems.map((i) => ({ id: i.id, price: i.price, qty: i.qty })),
    {
      config: {
        standardShippingFee: shippingSettings.standardShippingFee,
        expressShippingFee: shippingSettings.expressShippingFee,
        freeShippingThreshold: shippingSettings.freeShippingThreshold,
        gstRate: taxSettings.gstRate,
        pricesIncludeGst: taxSettings.pricesIncludeGst,
      },
    },
  );

  // Real-time input validators
  const validate = useCallback(() => {
    const errs: Record<string, string> = {};

    // Pincode validation: numeric only, exactly 6 digits
    const cleanedPincode = pincode.replace(/\D/g, "");
    if (!pincode) {
      errs.pincode = "Pincode is required";
    } else if (cleanedPincode.length !== 6) {
      errs.pincode = "Must be a 6-digit numeric pincode (e.g. 641008)";
    }

    // Phone validation: 10 digit Indian number
    const cleanedPhone = phone.replace(/\D/g, "");
    if (!phone) {
      errs.phone = "Phone number is required";
    } else if (cleanedPhone.length < 10) {
      errs.phone = "Must be a 10-digit mobile number";
    }

    // Email validation
    if (!email) {
      errs.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = "Enter a valid email (e.g. name@example.com)";
    }

    // Full name validation
    if (!fullName || fullName.trim().length < 2) {
      errs.fullName = "Please enter your full name";
    }

    // House No validation
    if (!street || street.trim().length < 3) {
      errs.street = "Please enter house / building address";
    }

    if (!city || city.trim().length < 2) {
      errs.city = "City is required";
    }

    if (!state || state.trim().length < 2) {
      errs.state = "State is required";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [pincode, phone, email, fullName, street, city, state]);

  // Re-run validation on value change
  useEffect(() => {
    validate();
  }, [validate]);

  // Handle Pincode input (only allow numbers)
  const handlePincodeChange = (val: string) => {
    const numericOnly = val.replace(/\D/g, "").slice(0, 6);
    setPincode(numericOnly);
    setTouched((prev) => ({ ...prev, pincode: true }));
  };

  // Handle Phone input
  const handlePhoneChange = (val: string) => {
    setPhone(val);
    setTouched((prev) => ({ ...prev, phone: true }));
  };

  // Auto pre-fill saved user details or address history
  useEffect(() => {
    if (user) {
      if (user.name && !fullName) setFullName(user.name);
      if (user.email && !email) setEmail(user.email);
      if (user.phone && !phone) setPhone(user.phone);

      const saved = getStoredAddress(user.id) || (user.email ? getStoredAddress(user.email) : null);
      if (saved) {
        if (saved.pincode && !pincode) setPincode(saved.pincode);
        if (saved.city && !city) setCity(saved.city);
        if (saved.state && !state) setState(saved.state);
        if (saved.address && !street) setStreet(saved.address);
        if (saved.phone && !phone) setPhone(saved.phone);
        if (saved.fullName && !fullName) setFullName(saved.fullName);
      }
    }
    // Every prefill guard is `!field`, so re-running after a field is filled is a no-op —
    // and listing them keeps the effect from prefilling over a field the user just cleared.
  }, [user, open, fullName, email, phone, pincode, city, state, street]);

  // Auto-fill City & State when 6-digit Indian Pincode is typed
  useEffect(() => {
    if (pincode.length === 6 && /^\d+$/.test(pincode)) {
      fetch(`https://api.postalpincode.in/pincode/${pincode}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data[0] && data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
            const po = data[0].PostOffice[0];
            setCity(po.District || po.Division);
            setState(po.State);
          }
        })
        .catch(() => {});
    }
  }, [pincode]);

  // Check if customer account is blocked
  useEffect(() => {
    let active = true;
    const checkBlocked = async () => {
      const activeEmail = email || user?.email;
      const activePhone = phone || user?.phone;
      const activeUserId = user?.id;

      if (!activeEmail && !activePhone && !activeUserId) {
        setBlockedError(null);
        return;
      }

      const res = await checkIsCustomerBlocked({
        email: activeEmail,
        phone: activePhone,
        userId: activeUserId,
      });

      if (active) {
        if (res.isBlocked) {
          setBlockedError(
            res.reason ||
              "Your customer account has been suspended by administration. Ordering is disabled.",
          );
        } else {
          setBlockedError(null);
        }
      }
    };

    checkBlocked();

    const handleCustomerSync = () => checkBlocked();
    window.addEventListener("customers_updated", handleCustomerSync);
    return () => {
      active = false;
      window.removeEventListener("customers_updated", handleCustomerSync);
    };
  }, [email, phone, user, open]);

  const handleProceedToPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields touched to show validation errors
    setTouched({
      pincode: true,
      phone: true,
      email: true,
      fullName: true,
      street: true,
      city: true,
      state: true,
    });

    if (!validate()) {
      return;
    }

    // Verify account is not suspended
    const blockCheck = await checkIsCustomerBlocked({
      email: email || user?.email,
      phone: phone || user?.phone,
      userId: user?.id,
    });

    if (blockCheck.isBlocked) {
      setBlockedError(
        blockCheck.reason ||
          "Your customer account has been suspended by administration. Ordering is disabled.",
      );
      return;
    }

    setIsSubmitting(true);

    const shippingAddrObj = {
      fullName: fullName || "Customer",
      address: `${street} ${area}`.trim(),
      city: city,
      state: state,
      pincode: pincode,
      phone: phone,
    };

    try {
      // 1. Authoritative Server-Side Order & Price Calculation
      const serverOrder = await createServerRazorpayOrder({
        items: resolvedItems.map((i) => ({ id: i.id, qty: i.qty, name: i.name, price: i.price })),
        shippingAddress: shippingAddrObj,
        userEmail: email,
        userPhone: phone,
        userId: user?.id,
        shippingMethod: "standard",
      });

      // 2. Open Razorpay Popup locked with Server Order ID
      await openRazorpayMagicCheckout({
        key: serverOrder.keyId,
        amount: serverOrder.amount / 100,
        order_id: serverOrder.razorpayOrderId,
        items: resolvedItems,
        prefill: {
          name: fullName,
          email: email,
          contact: phone,
          address: street,
          city: city,
          state: state,
          pincode: pincode,
        },
        onSuccess: async (resp) => {
          const dateStr = new Date().toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });

          const itemsSummary = resolvedItems.map((i) => `${i.name} × ${i.qty}`).join(", ");

          // 3. Server-Side Verification & Atomic Transaction
          try {
            const verifyResult = await verifyServerPayment({
              razorpay_order_id: resp.razorpay_order_id || serverOrder.razorpayOrderId,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              userId: user?.id,
              userEmail: email || user?.email || "customer@example.com",
              userPhone: phone || user?.phone || "",
              items: resolvedItems.map((i) => ({
                id: i.id,
                name: i.name,
                price: i.price,
                qty: i.qty,
                image: i.image,
              })),
              shippingAddress: shippingAddrObj,
              shippingMethod: "standard",
            });

            // The order reference is the server's. Without one there is nothing valid to
            // hand the customer or to highlight on the orders page.
            if (!verifyResult || !verifyResult.success || !verifyResult.orderId) {
              throw new Error("Payment verification rejected by server.");
            }

            const placedOrder: UserOrder = {
              id: verifyResult.orderId,
              userId: user?.id || "",
              userEmail: email || user?.email || "customer@example.com",
              userPhone: phone || user?.phone || "",
              date: dateStr,
              items: resolvedItems.map((i) => ({
                id: i.id,
                name: i.name,
                price: i.price,
                qty: i.qty,
                image: i.image,
              })),
              itemsSummary,
              subtotal: serverOrder.orderSummary?.subtotal ?? pricing.subtotal,
              shippingCost: serverOrder.orderSummary?.shippingFee ?? pricing.shippingFee,
              gst: serverOrder.orderSummary?.gstAmount ?? pricing.gstAmount,
              total: verifyResult.total ?? serverOrder.orderSummary?.total ?? pricing.finalTotal,
              status: "Processing",
              shippingAddress: shippingAddrObj,
              paymentMethod: "Razorpay",
            };

            await saveUserOrder(placedOrder);

            if (user?.id) saveAddress(user.id, shippingAddrObj);
            if (email) saveAddress(email, shippingAddrObj);
            if (typeof sessionStorage !== "undefined") {
              sessionStorage.setItem("recent_order_id", verifyResult.orderId);
            }

            trackMetaPurchase({
              orderId: verifyResult.orderId,
              total: verifyResult.total ?? serverOrder.orderSummary?.total ?? pricing.finalTotal,
              currency: "INR",
              items: resolvedItems.map((i) => ({
                id: i.id,
                name: i.name,
                price: i.price,
                qty: i.qty,
              })),
            });

            clear();
            setIsSubmitting(false);
            onClose();
            navigate({ to: "/orders" });
          } catch (verifyErr: unknown) {
            console.error("Payment verification failed:", verifyErr);
            setIsSubmitting(false);
            alert(
              (verifyErr as { message?: string }).message ||
                "Payment verification failed. If payment was deducted, please contact support with your payment ID: " +
                  resp.razorpay_payment_id,
            );
          }
        },
        onDismiss: () => {
          setIsSubmitting(false);
        },
      });
    } catch (err: unknown) {
      alert(
        (err as { message?: string }).message ||
          "Failed to initiate secure checkout. Please try again.",
      );
      setIsSubmitting(false);
    }
  };

  // Lock background scroll when modal is open
  useEffect(() => {
    if (open) {
      const origOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = origOverflow;
      };
    }
  }, [open]);

  if (!open || typeof document === "undefined" || !document.body) return null;

  const modalJSX = (
    <div
      className="fixed inset-0 z-[99999] overflow-y-auto bg-black/80 backdrop-blur-md p-2 sm:p-6 flex flex-col items-center justify-start sm:justify-center animate-in fade-in duration-200 overscroll-contain touch-pan-y"
      style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-5xl lg:max-w-6xl bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-stone-200 my-2 sm:my-auto grid grid-cols-1 lg:grid-cols-12 pointer-events-auto shrink-0 shadow-stone-900/20">
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close checkout modal"
          className="absolute right-3 top-3 sm:right-4 sm:top-4 z-30 grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-full bg-stone-100 text-stone-700 hover:bg-stone-200 transition-[color,background-color,border-color,box-shadow,transform,opacity] hover:scale-105 active:scale-95 shadow-sm"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Left Column: Order Summary Sidebar (Mason Home style) */}
        <div className="lg:col-span-5 bg-stone-50 p-4 sm:p-8 border-b lg:border-b-0 lg:border-r border-stone-200 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-900 text-white font-bold flex items-center justify-center text-base shadow-md">
                A
              </div>
              <div>
                <h3 className="font-display font-bold text-base sm:text-lg text-stone-900">
                  Aster Decorz
                </h3>
                <p className="text-xs text-emerald-800 font-semibold flex items-center gap-1.5 mt-0.5">
                  <ShieldCheck className="h-4 w-4" /> Razorpay Trusted Business
                </p>
              </div>
            </div>

            {/* Items summary card */}
            <div className="mt-4 sm:mt-8 bg-white p-3.5 sm:p-5 rounded-2xl border border-stone-200/90 shadow-sm space-y-3 sm:space-y-4">
              <p className="eyebrow text-xs tracking-wider text-stone-600 font-bold">
                Order Summary ({resolvedItems.length} items)
              </p>
              <div className="max-h-40 sm:max-h-64 overflow-y-auto divide-y divide-stone-100">
                {resolvedItems.map((item) => (
                  <div
                    key={item.id}
                    className="py-2 sm:py-3 flex items-center justify-between gap-3 sm:gap-4 text-xs sm:text-sm"
                  >
                    <div className="flex items-center gap-2.5 sm:gap-3">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-10 w-9 sm:h-14 sm:w-12 object-cover rounded-lg bg-stone-100 border border-stone-200 shrink-0"
                        />
                      )}
                      <div>
                        <p className="font-semibold text-stone-900 text-xs sm:text-sm line-clamp-1">
                          {item.name}
                        </p>
                        <p className="text-[11px] sm:text-xs text-stone-500 font-medium mt-0.5">
                          Quantity: {item.qty}
                        </p>
                      </div>
                    </div>
                    <span className="font-bold text-stone-900 tabular-nums text-xs sm:text-sm">
                      {formatPrice(item.price * item.qty)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-200 pt-3 sm:pt-4 space-y-2">
                <div className="flex items-center justify-between text-xs sm:text-sm text-stone-600">
                  <span>Subtotal</span>
                  <span className="tabular-nums font-medium">{formatPrice(pricing.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-xs sm:text-sm text-stone-600">
                  <span>Shipping</span>
                  <span className="tabular-nums font-medium">
                    {pricing.shippingFee === 0 ? "FREE" : formatPrice(pricing.shippingFee)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs sm:text-sm text-stone-600">
                  <span>
                    {pricing.pricesIncludeGst
                      ? `GST (${Math.round((taxSettings.gstRate || 0.18) * 100)}% included)`
                      : "Taxes (GST)"}
                  </span>
                  <span className="tabular-nums font-medium text-stone-500">
                    {formatPrice(pricing.gstAmount)}
                  </span>
                </div>

                <div className="pt-2 border-t border-stone-100 flex items-center justify-between font-bold text-stone-900">
                  <span className="text-sm">Final Total</span>
                  <span className="text-base sm:text-lg font-display text-emerald-900 tabular-nums">
                    {formatPrice(pricing.finalTotal)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Delivery Address Form */}
        <div className="lg:col-span-7 p-4 sm:p-10 flex flex-col justify-between bg-white">
          <div>
            <div className="flex items-center justify-between border-b border-stone-200 pb-4">
              <div>
                <h2 className="font-display font-bold text-xl sm:text-2xl text-stone-900">
                  Add delivery address
                </h2>
                <p className="text-xs text-stone-500 mt-1">
                  Enter your shipping details for 1-click Razorpay payment
                </p>
              </div>
              <span className="text-xs text-stone-500 font-semibold bg-stone-100 px-3 py-1 rounded-full">
                Step 1 of 2
              </span>
            </div>

            <form
              id="razorpay-address-form"
              onSubmit={handleProceedToPayment}
              className="mt-6 space-y-4"
            >
              {/* Pincode, City, State */}
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-5 sm:col-span-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={6}
                    placeholder="e.g. 641008"
                    value={pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, pincode: true }))}
                    className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                      touched.pincode && errors.pincode
                        ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                        : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                    }`}
                  />
                  {touched.pincode && errors.pincode && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                      {errors.pincode}
                    </p>
                  )}
                </div>
                <div className="col-span-7 sm:col-span-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                    City *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="City"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      setTouched((prev) => ({ ...prev, city: true }));
                    }}
                    onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
                    className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                      touched.city && errors.city
                        ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                        : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                    }`}
                  />
                  {touched.city && errors.city && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                      {errors.city}
                    </p>
                  )}
                </div>
                <div className="col-span-12 sm:col-span-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                    State *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="State"
                    value={state}
                    onChange={(e) => {
                      setState(e.target.value);
                      setTouched((prev) => ({ ...prev, state: true }));
                    }}
                    onBlur={() => setTouched((prev) => ({ ...prev, state: true }))}
                    className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                      touched.state && errors.state
                        ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                        : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                    }`}
                  />
                  {touched.state && errors.state && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                      {errors.state}
                    </p>
                  )}
                </div>
              </div>

              {/* Full Name & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Full name"
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      setTouched((prev) => ({ ...prev, fullName: true }));
                    }}
                    onBlur={() => setTouched((prev) => ({ ...prev, fullName: true }))}
                    className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                      touched.fullName && errors.fullName
                        ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                        : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                    }`}
                  />
                  {touched.fullName && errors.fullName && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                      {errors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setTouched((prev) => ({ ...prev, email: true }));
                    }}
                    onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                    className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                      touched.email && errors.email
                        ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                        : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                    }`}
                  />
                  {touched.email && errors.email && (
                    <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  placeholder="+91 Mobile number"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
                  className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                    touched.phone && errors.phone
                      ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                      : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                  }`}
                />
                {touched.phone && errors.phone && (
                  <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* House No / Building */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                  House No / Building / Apartment *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Door No, Flat, Building name"
                  value={street}
                  onChange={(e) => {
                    setStreet(e.target.value);
                    setTouched((prev) => ({ ...prev, street: true }));
                  }}
                  onBlur={() => setTouched((prev) => ({ ...prev, street: true }))}
                  className={`mt-1.5 min-h-12 w-full border rounded-xl px-4 text-sm font-medium outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity] ${
                    touched.street && errors.street
                      ? "border-rose-500 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-500/20"
                      : "border-stone-300 focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10"
                  }`}
                />
                {touched.street && errors.street && (
                  <p className="mt-1 text-[11px] font-semibold text-rose-600 animate-in fade-in">
                    {errors.street}
                  </p>
                )}
              </div>

              {/* Area / Street */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-stone-700">
                  Area / Sector / Street / Village
                </label>
                <input
                  type="text"
                  placeholder="Street name, landmark (optional)"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="mt-1.5 min-h-12 w-full border border-stone-300 rounded-xl px-4 text-sm font-medium outline-none focus:border-emerald-800 focus:ring-2 focus:ring-emerald-800/10 transition-[color,background-color,border-color,box-shadow,transform,opacity]"
                />
              </div>

              {/* Blocked / Suspended Customer Warning Banner */}
              {blockedError && (
                <div className="mt-4 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-start gap-3 animate-in fade-in">
                  <ShieldAlert className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-rose-950">Account Suspended</p>
                    <p className="mt-0.5 text-rose-800 leading-relaxed">{blockedError}</p>
                  </div>
                </div>
              )}

              {/* CTA Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !!blockedError}
                className={`mt-8 flex min-h-14 w-full items-center justify-center gap-3 font-bold text-sm uppercase tracking-[0.16em] rounded-2xl transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-lg ${
                  blockedError
                    ? "bg-rose-900/10 border border-rose-300 text-rose-800 cursor-not-allowed opacity-80"
                    : "bg-stone-900 text-white hover:bg-stone-800 active:scale-[0.99] hover:shadow-xl disabled:opacity-50 cursor-pointer"
                }`}
              >
                {blockedError ? (
                  <span>ORDERING SUSPENDED FOR THIS ACCOUNT</span>
                ) : (
                  <>
                    <span>Continue to Razorpay Payment</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalJSX, document.body);
}
